import {
  CirclesFourIcon,
  IdCardIcon,
  // MapIcon,
  SirenIcon,
  UserCheckIcon,
  UserIcon,
} from "@/assets/icons/Icons";
import { useIncidents } from "@/context/IncidentsContext";
import { MapPinCheckIcon } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Head from "../../assets/images/head.svg";
import UserProfile from "./UserProfile";

interface MenuItem {
  icon: JSX.Element;
  label: string;
  path: string;
  showCounter?: boolean;
  roles: string[];
}

interface SidebarProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
}

const Sidebar = ({ isMobileMenuOpen, setIsMobileMenuOpen }: SidebarProps) => {
  const location = useLocation();
  const { incidentCount, fetchIncidents } = useIncidents();
  const isActiveRoute = (path: string) => {
    if (path === "/alerts") {
      return location.pathname.startsWith("/alerts");
    }
    return location.pathname === path;
  };

  useEffect(() => {
    fetchIncidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const menuItems: MenuItem[] = [
    {
      icon: <CirclesFourIcon />,
      label: "Dashboard",
      path: "/",
      roles: ["Administrator", "Operator"],
    },
    //   label: "Map",
    //   path: "/map",
    //   roles: ["Administrator", "Operator"],
    {
      icon: <SirenIcon fill="white" />,
      label: "Alerts",
      path: "/alerts",
      showCounter: true,
      roles: ["Administrator", "Operator"],
    },
    {
      icon: <IdCardIcon />,
      label: "Team",
      path: "/teams",
      roles: ["Administrator"],
    },
    {
      icon: <UserIcon />,
      label: "Users",
      path: "/users",
      roles: ["Administrator"],
    },
    {
      icon: <UserCheckIcon />,
      label: "First Responders",
      path: "/first-responders",
      roles: ["Administrator"],
    },
    {
      icon: <MapPinCheckIcon className="dark:text-gray-200" />,
      label: "Locations",
      path: "/location",
      roles: ["Administrator"],
    },
  ];

  const userType = localStorage.getItem("userDetails")
    ? JSON.parse(localStorage.getItem("userDetails")!).userType
    : null;

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles?.includes(userType)
  );

  return (
    <>
      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <button
          className="fixed inset-0 bg-dark bg-opacity-50 z-40 lg:hidden transition-all duration-300 cursor-pointer"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col 
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"} 
        lg:translate-x-0 transition-transform duration-300 ease-in-out
         w-[90px] md:w-[96px] bg-dark text-white shadow-xl 
        h-full lg:h-screen lg:rounded-tr-[16px] lg:rounded-br-[16px]`}
      >
        {/* Logo or Brand */}
        <div className="flex items-center justify-center my-2" style={isMobileMenuOpen ? { marginTop: '6vh' } : { marginTop: 0 }}>
          <img alt="Guardian ANJEL" src={Head} />
        </div>

        {/* Menu Items */}
        <nav className="flex-1 pt-5 pb-4 lg:pt-2">
          <div className="space-y-1">
            {filteredMenuItems.map((item) => {
              const isActive = isActiveRoute(item.path);
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`group flex flex-col items-center justify-center p-2 cursor-pointer lg:w-[96px] h-[80px] no-underline
                    ${isActive
                      ? "bg-[#FFFFFF1A] text-white"
                      : "text-gray-300 dark:text-gray-700 hover:bg-[#FFFFFF1A] hover:text-white"
                    }`}
                >
                  <div className="relative">
                    {item.icon}
                    {item.showCounter && incidentCount > 0 && (
                      <span
                        className="absolute -top-3 -right-3 bg-red-600 text-white rounded-full text-xs flex items-center justify-center"
                        style={{
                          minWidth: "20px",
                          height: "20px",
                          padding: "0 4px",
                          lineHeight: "20px",
                          fontSize: "10px",
                        }}
                      >
                        {incidentCount}
                      </span>
                    )}
                  </div>
                  <span className="mt-1 text-overline text-center dark:text-gray-200">
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>


        {/* User Profile Section */}
        {/* <ModeToggle /> */}
        <UserProfile />
      </aside>
    </>
  );
};

export default Sidebar;
